import { randomId } from '../../util/randomId';
import type { DesignerLevel } from '@99designs/graph-utils/types';

interface FixtureUser {
    __typename: 'User';
    id: string;
    displayName: string;
    hasGeneratedDisplayname: boolean;
    fullName: string;
    email: string;
    isAdmin: boolean;
    isDesigner: boolean;
    isOnline: boolean;
    isSystem: boolean;
    designerLevel: DesignerLevel;
    isCertifiedDesigner: boolean;
    isPartnershipDesigner: boolean;
    certifications: string[];
    invite: {
        __typename: 'Invite';
        community: 'nationbuilder' | 'squarespace' | 'prezi';
    } | null;
    profile: {
        __typename: 'Profile';
        id: string;
        isAvailable: false;
        avatarUrl: string;
        biography: string;
        lastSeenAt: string;
        curationSamples: Array<{
            __typename: 'CurationDesign';
            id: number;
            image: string;
            title: string;
            description: string;
            crop: Array<number>;
        }>;
    };
    timezone: string;
    languages: Array<string>;
    socialMediaLinks: Array<string>;
    address: {
        __typename: 'Address';
        line_1: string;
        line_2: string;
        suburb: string;
        state: string;
        postcode: string;
        country: string;
    } | null;
    portfolioLinks: Array<string>;
    signedCodeOfConduct: boolean;
    hasRequestedDesignerCertification: boolean;
    hasRejectedDesignerCertification: boolean;
    designerApplicationEligibility: {
        __typename: 'DesignerApplicationEligibilityInfo';
        eligible: boolean;
    };
}

export const createUser = (props: Partial<FixtureUser>): FixtureUser => {
    const userId = props.id || randomId(1000, 100000) + '';
    return {
        __typename: 'User',
        id: userId,
        displayName: 'user-' + userId,
        hasGeneratedDisplayname: false,
        fullName: 'fullname-' + userId,
        email: 'user-' + userId + '@example.org',
        isSystem: false,
        isAdmin: false,
        isDesigner: false,
        isCertifiedDesigner: false,
        isPartnershipDesigner: false,
        isOnline: false,
        designerLevel: 'ENTRY',
        certifications: [],
        invite: null,
        profile: {
            __typename: 'Profile',
            id: userId,
            isAvailable: false,
            avatarUrl: '',
            biography: '',
            lastSeenAt: '',
            curationSamples: [],
        },
        timezone: '',
        languages: ['en'],
        socialMediaLinks: [],
        address: null,
        signedCodeOfConduct: false,
        portfolioLinks: [],
        hasRequestedDesignerCertification: false,
        hasRejectedDesignerCertification: false,
        designerApplicationEligibility: {
            __typename: 'DesignerApplicationEligibilityInfo',
            eligible: false,
        },
        ...props,
    };
};

export const client: FixtureUser = createUser({
    id: '101',
    displayName: 'customer',
    fullName: 'customer',
    email: 'customer@example.org',
});

export const designer: FixtureUser = createUser({
    id: '100',
    displayName: 'designer',
    fullName: 'designer',
    email: 'designer@example.org',
    isDesigner: true,
    designerLevel: 'ENTRY',
    isCertifiedDesigner: true,
    certifications: ['designer'],
});

export const admin: FixtureUser = createUser({
    id: '99',
    displayName: 'developer',
    fullName: 'developer',
    email: 'developer@example.org',
    isAdmin: true,
});

export const system: FixtureUser = createUser({
    id: '1',
    displayName: '99designs',
    fullName: 'User #1',
    email: 'user1@example.org',
    isAdmin: false,
    isSystem: true,
});
